import React, { useState, useEffect } from 'react';
import { FiSave, FiUser, FiPhone, FiMapPin, FiTool, FiActivity, FiCheckCircle, FiClock } from 'react-icons/fi';
import { Link } from 'react-router-dom'; 
import Swal from 'sweetalert2';
import MiniCalendar from '../../components/MiniCalendar';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function CreateJobPage() {
  const [jobsList, setJobsList] = useState([]);
  const [techList, setTechList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '', phone: '', job_type: '', problem: '', address: '',
    price: '', technicianId: '', appointmentDate: '', appointmentTime: '09:00'
  });

  useEffect(() => {
    fetchJobsData();
    fetchTechList();
  }, []);

  const fetchJobsData = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_all_jobs`);
      const result = await res.json();
      if (result.success) setJobsList(result.data);
    } catch (error) { console.error("ดึงข้อมูลสถิติไม่ได้:", error); }
  };

  const fetchTechList = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_users`);
      const data = await res.json();
      setTechList(data);
    } catch (error) { console.error("ดึงข้อมูลช่างไม่ได้:", error); }
  };

  const stats = {
    aircon: jobsList.filter(j => j.job_type === 'aircon').length,
    electric: jobsList.filter(j => j.job_type === 'electric').length,
    contractor: jobsList.filter(j => j.job_type === 'contractor' || j.job_type === 'extension').length,
    solar: jobsList.filter(j => j.job_type === 'solar').length,
    door: jobsList.filter(j => j.job_type === 'door').length,
    evertor: jobsList.filter(j => j.job_type === 'evertor').length,
    camera: jobsList.filter(j => j.job_type === 'camera').length,
    rework: jobsList.filter(j => j.job_type === 'rework').length,
    inProgress: jobsList.filter(j => ['pending', 'traveling', 'in_progress'].includes(j.status)).length,
    completed: jobsList.filter(j => j.status === 'completed').length,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.phone || !formData.job_type) {
      Swal.fire('แจ้งเตือน', 'กรุณากรอกชื่อ, เบอร์โทร และประเภทงาน', 'warning');
      return;
    }
    setIsLoading(true);
    Swal.fire({ title: 'กำลังบันทึกคิวงาน...', didOpen: () => Swal.showLoading() });

    try {
      let assignedTechName = 'ไม่ระบุ';
      if (formData.technicianId) {
        const ids = formData.technicianId.split(',');
        const names = ids.map(id => {
          const t = techList.find(tech => tech.uid === id);
          return t ? t.name : '';
        }).filter(Boolean);
        if (names.length > 0) assignedTechName = names.join(', ');
      }

      const status = formData.technicianId ? 'pending' : 'pending_approval';
      const payload = { ...formData, technicianName: assignedTechName, status: status };

      const res = await fetch(`${API_URL}?action=add_job`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const result = await res.json();
      
      if (result.success) {
        Swal.fire('สำเร็จ!', 'สร้างคิวงานใหม่เข้าระบบเรียบร้อยแล้ว', 'success');
        setFormData({ customerName: '', phone: '', job_type: '', problem: '', address: '', price: '', technicianId: '', appointmentDate: '', appointmentTime: '09:00' });
        fetchJobsData(); // โหลดข้อมูลตารางใหม่ทันที
      } else {
        Swal.fire('ผิดพลาด', result.message, 'error');
      }
    } catch (error) {
      Swal.fire('ผิดพลาด', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const StatRow = ({ label, count, colorClass, icon }) => (
    <div className={`flex justify-between items-center p-3 rounded-xl border shadow-sm transition-colors ${colorClass}`}>
      <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">{icon} {label}</span>
      <span className="text-lg font-black">{count}</span>
    </div>
  );

  // 🚀 ฟังก์ชันดึงชื่อประเภทงานสวยๆ สำหรับตาราง
  const getJobTypeLabel = (type) => {
    const types = { 
      'aircon': '❄️ แอร์', 'electric': '⚡ ไฟฟ้า', 'contractor': '🧱 รับเหมา', 
      'extension': '🏠 ต่อเติม', 'solar': '☀️ โซล่าเซลล์', 'door': '🚪 ประตูอัตโนมัติ', 
      'evertor': '🛗 ลิฟท์', 'camera': '📹 กล้องวงจรปิด', 'rework': '🔧 งานแก้'
    };
    return types[type] || 'ทั่วไป';
  };

  // 🚀 ตัดเอาเฉพาะ 10 งานล่าสุดมาแสดงในตาราง (ฐานข้อมูลดึงมาเรียงล่าสุดให้อยู่แล้ว)
  const recentJobs = jobsList.slice(0, 10);

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-10 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-gray-800 dark:text-white transition-colors">📝 สร้างคิวงาน</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors">รับเรื่องจากลูกค้า และสร้างคิวงานเข้าระบบรอจ่ายช่าง</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        
        {/* 📝 ฝั่งซ้าย: ฟอร์มคีย์งาน */}
        <div className="flex-1 w-full bg-white dark:bg-[#1e293b] p-6 md:p-8 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm transition-colors">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2"><FiUser /> ชื่อลูกค้า</label>
                <input type="text" value={formData.customerName} onChange={e => setFormData({...formData, customerName: e.target.value})} className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none transition-colors" placeholder="เช่น คุณสมชาย" required />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2"><FiPhone /> เบอร์โทรศัพท์</label>
                <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none transition-colors" placeholder="08X-XXX-XXXX" required />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2"><FiTool /> ประเภทงาน</label>
                <select value={formData.job_type} onChange={e => setFormData({...formData, job_type: e.target.value})} className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none font-bold" required>
                  <option value="">-- เลือกหมวดหมู่งาน --</option>
                  <option value="aircon">❄️ งานติดตั้ง/ล้างแอร์</option>
                  <option value="electric">⚡ งานระบบไฟฟ้า</option>
                  <option value="contractor">🧱 งานรับเหมาทั่วไป</option>
                  <option value="extension">🏠 งานต่อเติมบ้าน</option>
                  <option value="solar">☀️ โซล่าเซลล์</option>
                  <option value="door">🚪 ประตูอัตโนมัติ</option>
                  <option value="evertor">🛗 ลิฟท์</option>
                  <option value="camera">📹 กล้องวงจรปิด</option>
                  <option value="rework">🔧 งานแก้ (รับประกัน)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">เลือกทีมช่างผู้รับผิดชอบ (เลือกได้หลายคน)</label>
                <div className="grid grid-cols-2 gap-2 bg-gray-50 dark:bg-[#0f172a] p-3 rounded-lg border border-gray-300 dark:border-[#334155] max-h-36 overflow-y-auto custom-scrollbar">
                  {techList.map(t => {
                    const selectedIds = formData.technicianId ? formData.technicianId.split(',') : [];
                    const isChecked = selectedIds.includes(t.uid);
                    return (
                      <label key={t.uid} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-200 dark:hover:bg-[#1e293b] rounded transition text-xs">
                        <input 
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            let newIds = [...selectedIds];
                            if (isChecked) {
                              newIds = newIds.filter(id => id !== t.uid);
                            } else {
                              newIds.push(t.uid);
                            }
                            setFormData({ ...formData, technicianId: newIds.join(',') });
                          }}
                          className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer"
                        />
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{t.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">ราคาประเมิน (บาท)</label>
                <input type="number" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none" placeholder="0" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">ช่วงเวลาบริการ</label>
                <select value={formData.appointmentTime} onChange={e => setFormData({...formData, appointmentTime: e.target.value})} className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none font-bold">
                  <option value="09:00">ช่วงเช้า (09:00 - 12:00 น.)</option>
                  <option value="13:00">ช่วงบ่าย (13:00 - 16:00 น.)</option>
                  <option value="16:30">ช่วงเย็น (16:30 - 18:30 น.)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">เลือกวันที่นัดหมาย (สีเขียว = ว่าง, สีแดง = เต็ม)</label>
              <MiniCalendar 
                selectedDate={formData.appointmentDate} 
                onChange={dateStr => setFormData({...formData, appointmentDate: dateStr})} 
                allowPastDates={true} 
              />
              {formData.appointmentDate && (
                <div className="mt-2 text-xs font-bold text-blue-600 dark:text-blue-400">
                  วันที่เลือก: {formData.appointmentDate}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">รายละเอียดงาน / อาการเสีย</label>
              <textarea value={formData.problem} onChange={e => setFormData({...formData, problem: e.target.value})} rows="3" className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none" placeholder="ระบุรายละเอียดให้ช่างทราบ..."></textarea>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2"><FiMapPin /> สถานที่หน้างาน (ที่อยู่)</label>
              <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} rows="2" className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg outline-none" placeholder="บ้านเลขที่, หมู่บ้าน, ซอย, ถนน..."></textarea>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-[#334155] flex justify-end">
              <button type="submit" disabled={isLoading} className="w-full md:w-auto px-10 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50">
                <FiSave /> {isLoading ? 'กำลังบันทึก...' : 'บันทึกและสร้างคิวงาน'}
              </button>
            </div>
          </form>
        </div>

        {/* 📊 ฝั่งขวา: สถิติงาน */}
        <div className="w-full lg:w-72 shrink-0 flex flex-col gap-3">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1 ml-1">สถิติงานทั้งหมด</p>
          <StatRow label="กำลังดำเนินการ" count={stats.inProgress} icon={<FiActivity className="animate-pulse"/>} colorClass="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50" />
          <StatRow label="สำเร็จแล้ว" count={stats.completed} icon={<FiCheckCircle/>} colorClass="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/50" />
          
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 mt-3 mb-1 ml-1 uppercase">แยกตามหมวดหมู่</p>
          <StatRow label="แอร์" count={stats.aircon} icon="❄️" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="ไฟฟ้า" count={stats.electric} icon="⚡" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="รับเหมา/ต่อเติม" count={stats.contractor} icon="🧱" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="โซล่าเซลล์" count={stats.solar} icon="☀️" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="ประตูไฟฟ้า" count={stats.door} icon="🚪" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="ลิฟท์" count={stats.evertor} icon="🛗" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="กล้องวงจรปิด" count={stats.camera} icon="📹" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
          <StatRow label="งานแก้" count={stats.rework} icon="🔧" colorClass="bg-white text-gray-700 border-gray-200 dark:bg-[#1e293b] dark:text-gray-300 dark:border-[#334155]" />
        </div>
      </div>

      {/* 🚀 ส่วนที่ 3 (ใหม่ล่าสุด): ตารางแสดงข้อมูลการคีย์งาน 10 รายการล่าสุด */}
      <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors mt-8">
        
        {/* หัวตารางและปุ่มดูเพิ่มเติม */}
        <div className="p-4 border-b border-gray-200 dark:border-[#334155] bg-gray-50 dark:bg-[#0f172a]/50 flex flex-col sm:flex-row justify-between items-center gap-4 transition-colors">
          <h2 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FiClock className="text-blue-500" /> ข้อมูลลูกค้าย้อนหลัง (10 งานล่าสุด)
          </h2>
          <Link to="/admin/jobs/all" className="bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-800/50 px-4 py-2 rounded-lg text-xs font-bold transition-colors">
            ดูข้อมูลงานทั้งหมด ➡️
          </Link>
        </div>

        {/* ตารางข้อมูล */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
            <thead className="text-xs uppercase bg-white dark:bg-[#1e293b] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#334155]">
              <tr>
                <th className="px-4 py-3 font-medium">ชื่อลูกค้า</th>
                <th className="px-4 py-3 font-medium">เบอร์โทรศัพท์</th>
                <th className="px-4 py-3 font-medium">ประเภทงาน</th>
                <th className="px-4 py-3 font-medium">ที่อยู่หน้างาน</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#334155]">
              {recentJobs.length > 0 ? recentJobs.map(job => (
                <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-[#0f172a]/50 transition-colors">
                  <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">{job.customerName || 'ไม่ระบุชื่อ'}</td>
                  <td className="px-4 py-3 text-blue-600 dark:text-blue-400 font-medium">{job.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-bold border border-gray-200 dark:border-gray-700">
                      {getJobTypeLabel(job.job_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate text-gray-500 dark:text-gray-400" title={job.address}>
                    {job.address || '-'}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-gray-500 font-medium">ยังไม่มีประวัติการคีย์งานในระบบ</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}